import React, { createRef, useEffect } from "react";
import "./App.scss";
import styled from "styled-components";
import { AppHeader } from "./components/app-header/AppHeader";
import { AppFooter } from "./components/app-footer/AppFooter";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useJsApiLoader } from '@react-google-maps/api'
import { MainPage } from "./pages/main-page/MainPage";
import { Paths } from "./paths";
import { AboutPage } from "./pages/about/AboutPage";
import { AdvancedSearch } from "./pages/advanced-search/AdvancedSearch";
import { ContestPage } from "./pages/contest/Contest";
import { PrivacyPage } from "./pages/privacy/PrivacyPage";
import { FaqPage } from "./pages/faq/FaqPage";
import { OldVinesInfoPage } from "./pages/old-vines-info/OldVinesInfoPage";
import { SubmitPage } from "./pages/submit/SubmitPage";
import { FeedbackPage } from "./pages/feedback/FeedbackPage";
import "bootstrap/dist/css/bootstrap.min.css";
import { VineyardsTable } from "./pages/vineyards-table/VineyardsTable";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Vineyard } from "./pages/vineyard/Vineyard";
import { ThankYouPage } from "./pages/thank-you/ThankYouPage";
import { AdminPage } from "./pages/admin/AdminPage";
import { Guarded } from "./components/guard/Guarded";
import { authGuard } from "./guards/authGuard";
import { LoginPage } from "./pages/login/LoginPage";
import { NotFound } from "./pages/notfound/NotFound";
import { Box } from "./components/styled-system/Box";
import { ErrorsTable } from "./pages/admin/errors/ErrorPage";
import { SubmissionsTable } from "./pages/admin/submissions/SubmissionPage";
import { SearchHistoryTable } from "./pages/admin/search-history/SearchHistory";
import { SealLandingPage } from "./pages/seal/landing/SealLandingPage";
import { SealFaqPage } from "./pages/seal/faq/SealFaqPage";
import { SealGuidelinesPage } from "./pages/seal/guidelines/SealGuidelinesPage";
import { SealRegistrationLayout } from "./pages/seal/registration/SealRegistrationLayout";
import { SealRegistrationPage } from "./pages/seal/registration/SealRegistrationPage";
import { SealPaymentPage } from "./pages/seal/registration/SealPaymentPage";
import { SealConfirmationPage } from "./pages/seal/registration/SealConfirmationPage";

const AppLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Content = (props: { children: React.ReactNode }) => {
  const containerElmRef = createRef<HTMLElement>();
  const { pathname } = useLocation();
  //Scroll to top when the path changes
  useEffect(() => {
    containerElmRef.current?.scrollTo({ top: 0 });
  }, [pathname, containerElmRef]);

  return (
    <Box ref={containerElmRef} flex="1" overflow="auto">
      {props.children}
    </Box>
  );
};

const queryClient = new QueryClient();

function App() {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!
  });

  if (loadError) return <div>Error loading maps</div>
  if (!isLoaded) return <div></div>
  
  return (
     <HelmetProvider>
      <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <AppLayout>
                    <AppHeader />
                    <Content>
                      <Outlet />
                      <AppFooter />
                    </Content>
                  </AppLayout>
                }
              >
                <Route index element={<MainPage />} />
                <Route path={Paths.about} element={<AboutPage />} />
                <Route path={Paths.advancedSearch} element={<AdvancedSearch/>} />
                <Route path={Paths.contest} element={<ContestPage />} />
                <Route path={Paths.oldVinesInfo} element={<OldVinesInfoPage />} />
                <Route path={Paths.submit} element={<SubmitPage />} />
                <Route path={Paths.feedback} element={<FeedbackPage />} />
                <Route path={Paths.privacy} element={<PrivacyPage />} />
                <Route path={Paths.faq} element={<FaqPage />} />
                <Route path={Paths.thankYou} element={<ThankYouPage />} />
                <Route path={Paths.vineyards} element={<VineyardsTable />} />
                <Route path={Paths.vineyard} element={<Vineyard />} />
                <Route path={Paths.errors} element={<ErrorsTable />} />
                <Route path={Paths.submissions} element={<SubmissionsTable />} />
                <Route path={Paths.searchHistory} element={<SearchHistoryTable />} />
                <Route path={Paths.seal} element={<SealLandingPage />} />
                <Route path={Paths.sealFaq} element={<SealFaqPage />} />
                <Route path={Paths.sealGuidelines} element={<SealGuidelinesPage />} />
                <Route element={<SealRegistrationLayout />}>
                  <Route path={Paths.sealRegister} element={<SealRegistrationPage />} />
                  <Route path={Paths.sealPayment} element={<SealPaymentPage />} />
                  <Route path={Paths.sealConfirmation} element={<SealConfirmationPage />} />
                </Route>
                <Route
                  path={Paths.admin}
                  element={
                    <Guarded guards={[authGuard]}>
                      <AdminPage />
                    </Guarded>
                  }
                />
              </Route>
              <Route path={Paths.login} element={<LoginPage />} />
              {/* Catch-all route for 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider> 
  );
}

export default App;